class CartRemoveButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      if (cartItems && typeof cartItems.updateQuantity === 'function') {
        cartItems.updateQuantity(this.dataset.index, 0);
      }
    });
  }
}

if (!customElements.get('cart-remove-button')) {
  customElements.define('cart-remove-button', CartRemoveButton);
}

class CartItems extends HTMLElement {
  constructor() {
    super();

    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') ||
      document.getElementById('CartDrawer-LineItemStatus');

    this.currentItemCount = Array.from(this.querySelectorAll('[name="updates[]"]'))
      .reduce((total, quantityInput) => total + (parseInt(quantityInput.value, 10) || 0), 0);

    this.debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, 300);

    this.addEventListener('change', this.debouncedOnChange.bind(this));
  }

  onChange(event) {
    const input = event.target;
    if (!input || !input.dataset || !input.dataset.index) return;

    const activeName =
      document.activeElement && document.activeElement.getAttribute
        ? document.activeElement.getAttribute('name')
        : null;

    const previousQuantity = input.defaultValue;

    this.updateQuantity(
      input.dataset.index,
      input.value,
      activeName,
      previousQuantity
    );
  }

  getSectionsToRender() {
    const sections = [];

    const mainCartItems = document.getElementById('main-cart-items');
    if (mainCartItems && mainCartItems.dataset.id) {
      sections.push({
        id: 'main-cart-items',
        section: mainCartItems.dataset.id,
        selector: '.js-contents',
      });
    }

    sections.push({
      id: 'cart-icon-bubble',
      section: 'cart-icon-bubble',
      selector: '.shopify-section',
    });

    const liveRegion = document.getElementById('cart-live-region-text');
    if (liveRegion) {
      sections.push({
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      });
    }

    const mainCartFooter = document.getElementById('main-cart-footer');
    if (mainCartFooter && mainCartFooter.dataset.id) {
      sections.push({
        id: 'main-cart-footer',
        section: mainCartFooter.dataset.id,
        selector: '.js-contents',
      });
    }

    return sections;
  }

  getCartChangeUrl() {
    const root =
      window.Shopify &&
      window.Shopify.routes &&
      window.Shopify.routes.root
        ? window.Shopify.routes.root
        : '/';

    return `${root.replace(/\/?$/, '/')}cart/change.js`;
  }

  wait(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  async requestCartChange(body) {
    const url = this.getCartChangeUrl();
    let lastError = null;

    for (let attempt = 1; attempt <= 2; attempt += 1) {
      try {
        const response = await fetch(url, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body,
        });

        const stateText = await response.text();
        const contentType = (response.headers.get('content-type') || '').toLowerCase();
        const trimmed = stateText.trim();

        const looksLikeJson =
          contentType.includes('application/json') ||
          trimmed.startsWith('{') ||
          trimmed.startsWith('[');

        if (!looksLikeJson) {
          const isVerificationPage =
            /verifying your connection/i.test(stateText) ||
            /<!doctype html/i.test(stateText) ||
            /<html/i.test(stateText);

          const error = new Error(
            isVerificationPage
              ? 'SHOPIFY_CONNECTION_VERIFICATION'
              : 'NON_JSON_CART_RESPONSE'
          );

          error.code = isVerificationPage
            ? 'SHOPIFY_CONNECTION_VERIFICATION'
            : 'NON_JSON_CART_RESPONSE';

          throw error;
        }

        let parsedState;

        try {
          parsedState = JSON.parse(stateText);
        } catch (jsonError) {
          const error = new Error('INVALID_JSON_CART_RESPONSE');
          error.code = 'INVALID_JSON_CART_RESPONSE';
          throw error;
        }

        if (!response.ok || parsedState.errors) {
          const serverMessage =
            typeof parsedState.errors === 'string'
              ? parsedState.errors
              : parsedState.description || window.cartStrings.error;

          const error = new Error(serverMessage);
          error.code = 'CART_API_ERROR';
          throw error;
        }

        return parsedState;
      } catch (error) {
        lastError = error;

        const retryable =
          error instanceof TypeError ||
          error.code === 'SHOPIFY_CONNECTION_VERIFICATION' ||
          error.code === 'NON_JSON_CART_RESPONSE' ||
          error.code === 'INVALID_JSON_CART_RESPONSE';

        if (attempt < 2 && retryable) {
          await this.wait(900);
          continue;
        }

        throw error;
      }
    }

    throw lastError || new Error('CART_UPDATE_FAILED');
  }

  async updateQuantity(line, quantity, name = null, previousQuantity = null) {
    this.enableLoading(line);

    try {
      const sectionsToRender = this.getSectionsToRender();

      const body = JSON.stringify({
        line: parseInt(line, 10),
        quantity: parseInt(quantity, 10),
        sections: sectionsToRender.map((section) => section.section),
        sections_url: window.location.pathname,
      });

      const parsedState = await this.requestCartChange(body);

      this.classList.toggle('is-empty', parsedState.item_count === 0);

      const cartDrawerWrapper = document.querySelector('cart-drawer');
      const cartFooter = document.getElementById('main-cart-footer');

      if (cartFooter) {
        cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
      }

      if (cartDrawerWrapper) {
        cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);
      }

      sectionsToRender.forEach((section) => {
        const sectionHTML =
          parsedState.sections && parsedState.sections[section.section];

        if (!sectionHTML) return;

        const root = document.getElementById(section.id);
        if (!root) return;

        const elementToReplace =
          (section.selector && root.querySelector(section.selector)) || root;

        const newHTML = this.getSectionInnerHTML(
          sectionHTML,
          section.selector
        );

        if (newHTML !== null) {
          elementToReplace.innerHTML = newHTML;
        }
      });

      this.updateLiveRegions(line, parsedState.item_count);

      const lineItem =
        document.getElementById(`CartItem-${line}`) ||
        document.getElementById(`CartDrawer-Item-${line}`);

      const focusTarget =
        lineItem && name
          ? lineItem.querySelector(`[name="${name}"]`)
          : null;

      if (focusTarget) {
        if (cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, focusTarget);
        } else {
          focusTarget.focus();
        }
      } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
        const emptyDrawer =
          cartDrawerWrapper.querySelector('.drawer__inner-empty');
        const emptyLink =
          cartDrawerWrapper.querySelector('.drawer__inner-empty a');

        if (emptyDrawer && emptyLink) {
          trapFocus(emptyDrawer, emptyLink);
        }
      } else if (cartDrawerWrapper) {
        const firstCartItemName =
          cartDrawerWrapper.querySelector('.cart-item__name');

        if (firstCartItemName) {
          trapFocus(cartDrawerWrapper, firstCartItemName);
        }
      }

      const errors =
        document.getElementById('cart-errors') ||
        document.getElementById('CartDrawer-CartErrors');

      if (errors) {
        errors.textContent = '';
      }

      document.dispatchEvent(
        new CustomEvent('cart:refresh', {
          detail: { cart: parsedState },
        })
      );
    } catch (error) {
      console.error('Cart update error:', error);

      document
        .querySelectorAll('.loading-overlay')
        .forEach((overlay) => overlay.classList.add('hidden'));

      const quantityInput =
        document.getElementById(`Quantity-${line}`) ||
        document.getElementById(`Drawer-quantity-${line}`);

      if (
        quantityInput &&
        previousQuantity !== null &&
        previousQuantity !== undefined
      ) {
        quantityInput.value = previousQuantity;
      }

      const errors =
        document.getElementById('cart-errors') ||
        document.getElementById('CartDrawer-CartErrors');

      if (errors) {
        const isArabic =
          (document.documentElement.lang || '').toLowerCase().startsWith('ar');

        if (
          error.code === 'SHOPIFY_CONNECTION_VERIFICATION' ||
          error.code === 'NON_JSON_CART_RESPONSE' ||
          error.code === 'INVALID_JSON_CART_RESPONSE'
        ) {
          errors.textContent = isArabic
            ? 'تعذر تحديث السلة مؤقتًا. يرجى المحاولة مرة أخرى.'
            : 'We couldn’t update your cart right now. Please try again.';
        } else {
          errors.textContent =
            error && error.message
              ? error.message
              : window.cartStrings.error;
        }
      }
    } finally {
      this.disableLoading();
    }
  }

  updateLiveRegions(line, itemCount) {
    if (this.currentItemCount === itemCount) {
      const lineItemError =
        document.getElementById(`Line-item-error-${line}`) ||
        document.getElementById(`CartDrawer-LineItemError-${line}`);

      const quantityElement =
        document.getElementById(`Quantity-${line}`) ||
        document.getElementById(`Drawer-quantity-${line}`);

      if (lineItemError && quantityElement) {
        const errorText = lineItemError.querySelector('.cart-item__error-text');
        if (errorText) {
          errorText.innerHTML = window.cartStrings.quantityError.replace(
            '[quantity]',
            quantityElement.value
          );
        }
      }
    }

    this.currentItemCount = itemCount;

    if (this.lineItemStatusElement) {
      this.lineItemStatusElement.setAttribute('aria-hidden', true);
    }

    const cartStatus =
      document.getElementById('cart-live-region-text') ||
      document.getElementById('CartDrawer-LiveRegionText');

    if (cartStatus) {
      cartStatus.setAttribute('aria-hidden', false);
      setTimeout(() => {
        cartStatus.setAttribute('aria-hidden', true);
      }, 1000);
    }
  }

  getSectionInnerHTML(html, selector) {
    if (!html) return null;

    const doc = new DOMParser().parseFromString(html, 'text/html');

    if (!selector) {
      return doc.body ? doc.body.innerHTML : null;
    }

    const selected = doc.querySelector(selector);
    return selected ? selected.innerHTML : null;
  }

  enableLoading(line) {
    const mainCartItems =
      document.getElementById('main-cart-items') ||
      document.getElementById('CartDrawer-CartItems');

    if (mainCartItems) {
      mainCartItems.classList.add('cart__items--disabled');
    }

    const cartItemElements = this.querySelectorAll(
      `#CartItem-${line} .loading-overlay`
    );
    const cartDrawerItemElements = this.querySelectorAll(
      `#CartDrawer-Item-${line} .loading-overlay`
    );

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) =>
      overlay.classList.remove('hidden')
    );

    if (document.activeElement && document.activeElement.blur) {
      document.activeElement.blur();
    }

    if (this.lineItemStatusElement) {
      this.lineItemStatusElement.setAttribute('aria-hidden', false);
    }
  }

  disableLoading() {
    const mainCartItems =
      document.getElementById('main-cart-items') ||
      document.getElementById('CartDrawer-CartItems');

    if (mainCartItems) {
      mainCartItems.classList.remove('cart__items--disabled');
    }
  }
}

if (!customElements.get('cart-items')) {
  customElements.define('cart-items', CartItems);
}

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'change',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, {
              ...fetchConfig(),
              body,
            }).catch((error) => console.error('Cart note update error:', error));
          }, 300)
        );
      }
    }
  );
}

function initCartDetails() {
  const detailsElement = document.querySelector('.cart-group');
  if (!detailsElement) return;

  const details = detailsElement.querySelectorAll('details');

  details.forEach((targetDetail) => {
    targetDetail.addEventListener('click', () => {
      details.forEach((detail) => {
        if (detail !== targetDetail) {
          detail.removeAttribute('open');
        }
      });
    });
  });

  const summaries = detailsElement.querySelectorAll('summary');

  summaries.forEach((summary) => {
    summary.addEventListener('click', function () {
      summaries.forEach((otherSummary) => {
        const detail = otherSummary.parentNode;
        if (detail !== this.parentNode) {
          detail.removeAttribute('open');
        }
      });
    });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCartDetails);
} else {
  initCartDetails();
}

